/**
 * P0AlertBanner — urgent-alert band shown inside the Inbox Actions
 * (maildesk-triage) module when there's at least one open + unclaimed
 * Type F (urgent) MailDesk triage item.
 *
 * Sprint 27 Phase 2 (v10 spec §7); scoped to /maildesk-triage on
 * 2026-05-10 — the global mount distracted users in every other
 * module, including ones who couldn't act on the alert.
 *
 * Polls `/api/maildesk-triage/alerts/active` every 30s. Shows the
 * first urgent item with a "+N more" chip if multiple are open.
 * "View Now" scrolls/highlights the row. "Acknowledge" calls the
 * alert-claim endpoint (server-side: sets `assigned_to`, which
 * removes the row from the alerts feed → banner advances or
 * unmounts).
 *
 * Visibility (Phase 2 D-1): admin, sales, operations, regulatory —
 * AND only on the /maildesk-triage route. Server-side gate on the
 * acknowledge endpoint enforces RBAC independently.
 *
 * Inlined fetchers (rather than importing
 * `@/modules/maildesk-triage/api/queries`) so the shell stays
 * compliant with the `no-restricted-imports` cross-module boundary
 * rule. Same endpoint path, separate query key.
 */

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useLocation, useNavigate } from '@tanstack/react-router'
import { AlertTriangle } from 'lucide-react'

import { Button } from '@/components/ui/Button'
import { jsonFetch } from '@/lib/api/jsonFetch'
import { useAuth, useHasAnyPermission } from '@/shell/module-system'

interface AlertItem {
  id: string
  thread_id: string
  customer_name: string | null
  sender_email: string | null
  subject: string | null
  inboxes: string[]
  latest_msg_at: string | null
}

interface AlertsResponse {
  items: AlertItem[]
  count: number
}

const ALERTS_KEY = ['maildesk-triage-alerts'] as const

function useActiveAlerts(enabled: boolean) {
  return useQuery({
    queryKey: ALERTS_KEY,
    queryFn: () =>
      jsonFetch<AlertsResponse>('/api/maildesk-triage/alerts/active'),
    enabled,
    refetchInterval: 30_000,
    // Unauthenticated visitors and viewer-only roles will get 401/403
    // intermittently as auth state mutates — don't blow up the shell.
    retry: false,
  })
}

function useAcknowledge() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (alertId: string) =>
      jsonFetch<AlertItem>(
        `/api/maildesk-triage/alerts/${alertId}/acknowledge`,
        {
          method: 'POST',
          body: {},
        },
      ),
    onSuccess: () => qc.invalidateQueries({ queryKey: ALERTS_KEY }),
  })
}

function formatTime(iso: string | null): string {
  if (!iso) return ''
  const t = Date.parse(iso)
  if (Number.isNaN(t)) return ''
  return new Date(t).toLocaleTimeString(undefined, {
    hour: 'numeric',
    minute: '2-digit',
  })
}

function formatTitle(item: AlertItem): string {
  const who = item.customer_name || item.sender_email || '(unknown sender)'
  const inbox = item.inboxes?.[0] ? ` [${item.inboxes[0]}]` : ''
  return `${who}${inbox}`
}

export function P0AlertBanner() {
  // RBAC: only roles that act on triage items see the banner.
  const canSee = useHasAnyPermission([
    'admin',
    'sales',
    'operations', 'purchasing',
    'regulatory',
  ])
  // Route gate (2026-05-10): only render inside the Inbox Actions
  // module. Outside that surface the banner was distracting + offered
  // no in-context action beyond "View Now" anyway.
  const location = useLocation()
  const onTriagePage = location.pathname.startsWith('/maildesk-triage')

  // Don't even start polling for users who'd never act on the alert.
  const auth = useAuth()
  const enabled = Boolean(canSee && onTriagePage && auth.uid)

  const alerts = useActiveAlerts(enabled)
  const ack = useAcknowledge()
  const navigate = useNavigate()

  if (!canSee || !onTriagePage) return null
  const items = alerts.data?.items ?? []
  if (items.length === 0) return null

  const first = items[0]!
  const moreCount = items.length - 1
  const ackBusy = ack.isPending && ack.variables === first.id

  return (
    <div
      role="alert"
      aria-live="assertive"
      className="flex items-center gap-3 border-b border-danger-border bg-danger-bg px-6 py-2 text-danger-text"
    >
      <AlertTriangle size={18} className="shrink-0" />
      <span className="text-sm">
        <span className="font-semibold">URGENT — {formatTitle(first)}</span>
        {first.subject ? (
          <span className="ml-2">· {first.subject}</span>
        ) : null}
        {first.latest_msg_at ? (
          <span className="ml-2">· {formatTime(first.latest_msg_at)}</span>
        ) : null}
        {moreCount > 0 ? (
          <span className="ml-3 inline-flex items-center rounded-full bg-danger-text px-2 py-0.5 text-xs font-semibold text-danger-bg">
            +{moreCount} more
          </span>
        ) : null}
      </span>
      <div className="ml-auto flex items-center gap-2">
        <Button
          variant="secondary"
          size="sm"
          onClick={() =>
            navigate({
              to: '/maildesk-triage',
              search: { itemId: first.id } as never,
            })
          }
        >
          View Now
        </Button>
        <Button
          variant="primary"
          size="sm"
          onClick={() => ack.mutate(first.id)}
          disabled={ackBusy}
        >
          {ackBusy ? 'Acknowledging…' : 'Acknowledge'}
        </Button>
      </div>
    </div>
  )
}
